<script setup lang="ts">
import { ActionButton, MenuItem, MenuPopover } from '@taler/ui'
import { compareLocalDate, type Locale, formatCount, formatDate } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import CarEventAmount from '@/features/garage/components/CarEventAmount.vue'
import CarEventIcon from '@/features/garage/components/CarEventIcon.vue'
import { categoryOf, eventDescription } from '../internal/car-presentation.ts'
import type { CarEvent } from '../internal/types.ts'

const props = defineProps<{
  events: readonly CarEvent[]
  accountNames: ReadonlyMap<string, string>
  canManage: boolean
  canLog: boolean
}>()
const emit = defineEmits<{ log: []; edit: [eventId: string]; delete: [eventId: string] }>()

const RECENT_LIMIT = 8

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as Locale)

const shown = computed(() =>
  [...props.events].sort((a, b) => compareLocalDate(b.date, a.date)).slice(0, RECENT_LIMIT),
)

function eventLabel(type: CarEvent['type']): string {
  return t(`garage.eventType.${type}`)
}

const CELL_CLASS = 'px-2 py-2.5 align-middle text-[color:var(--color-text)]'
const HEAD_CLASS =
  'whitespace-nowrap px-2 py-2 text-left text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]'
</script>

<template>
  <section
    class="flex flex-col gap-3 rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-4"
  >
    <div class="flex flex-wrap items-center justify-between gap-3">
      <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
        {{ t('garage.events.recentTitle') }}
      </h2>
      <ActionButton v-if="canLog" type="button" @click="emit('log')">
        {{ t('garage.events.logEvent') }}
      </ActionButton>
    </div>

    <p v-if="events.length === 0" class="text-[color:var(--color-text-muted)]">
      {{ t('garage.events.empty') }}
    </p>

    <div v-else class="relative overflow-x-auto">
      <table class="w-full border-collapse text-[length:var(--text-step-2)]">
        <thead class="border-b border-[color:var(--color-border)]">
          <tr>
            <th scope="col" :class="HEAD_CLASS">{{ t('garage.events.columns.date') }}</th>
            <th scope="col" :class="HEAD_CLASS">{{ t('garage.events.columns.type') }}</th>
            <th scope="col" :class="HEAD_CLASS">{{ t('garage.events.columns.description') }}</th>
            <th scope="col" :class="[HEAD_CLASS, 'text-right']">{{ t('garage.events.columns.odometer') }}</th>
            <th scope="col" :class="[HEAD_CLASS, 'text-right']">{{ t('garage.events.columns.amount') }}</th>
            <th scope="col" :class="HEAD_CLASS">{{ t('garage.events.columns.account') }}</th>
            <th scope="col" :class="HEAD_CLASS">{{ t('garage.events.columns.notes') }}</th>
            <th scope="col" class="w-10">
              <span class="sr-only">{{ t('garage.moreActions') }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="event in shown"
            :key="event.id"
            class="border-b border-[color:var(--color-border)] last:border-0 hover:bg-[color:var(--color-surface-raised)]"
          >
            <td :class="[CELL_CLASS, 'whitespace-nowrap tabular-nums']">
              {{ formatDate(event.date, currentLocale) }}
            </td>
            <td :class="CELL_CLASS">
              <span class="flex items-center gap-2 whitespace-nowrap">
                <CarEventIcon :category="categoryOf(event.type)" />
                {{ eventLabel(event.type) }}
              </span>
            </td>
            <td :class="CELL_CLASS">{{ eventDescription(event) ?? '—' }}</td>
            <td :class="[CELL_CLASS, 'whitespace-nowrap text-right tabular-nums']">
              {{ event.mileageKm === undefined ? '—' : `${formatCount(event.mileageKm, currentLocale)} km` }}
            </td>
            <td :class="[CELL_CLASS, 'whitespace-nowrap text-right']">
              <CarEventAmount :event="event" />
            </td>
            <td :class="CELL_CLASS">
              {{
                event.expenseAccountId === undefined ? '—' : (accountNames.get(event.expenseAccountId) ?? '—')
              }}
            </td>
            <td :class="CELL_CLASS">{{ event.notes ?? '—' }}</td>
            <td :class="[CELL_CLASS, 'text-right']">
              <MenuPopover v-if="canManage" side="bottom" align="end">
                <template #trigger>
                  <button
                    type="button"
                    class="min-h-[var(--density-target-min)] min-w-[var(--density-target-min)] rounded-[var(--radius-control)] text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-surface-raised)]"
                    :aria-label="`${t('garage.moreActions')} ${eventLabel(event.type)}`"
                  >
                    ⋯
                  </button>
                </template>
                <MenuItem @select="emit('edit', event.id)">{{ t('garage.events.edit') }}</MenuItem>
                <MenuItem class="text-[color:var(--color-danger)]" @select="emit('delete', event.id)">
                  {{ t('garage.events.delete') }}
                </MenuItem>
              </MenuPopover>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>
